import { alpha } from '@mui/material/styles';

// --- Premium Color Palette ---
const colors = {
  dark: {
    bgDefault: '#09090b',     // Ultra dark matte grey, not pitch black
    bgPaper: '#18181b',       // Zinc 900
    primaryMain: '#6366f1',   // Indigo 500 (Modern, trustworthy)
    primaryLight: '#818cf8',
    secondaryMain: '#10b981', // Emerald 500 (Growth, Success)
    textPrimary: '#fafafa',
    textSecondary: '#a1a1aa',
    border: 'rgba(255, 255, 255, 0.08)',
    gradient: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)', // Indigo to Violet
    cardGradient: 'linear-gradient(180deg, rgba(39, 39, 42, 0.8) 0%, rgba(24, 24, 27, 1) 100%)'
  },
  light: {
    bgDefault: '#f3f4f6',     // Cool Gray 100
    bgPaper: '#ffffff',
    primaryMain: '#4f46e5',   // Indigo 600
    primaryLight: '#6366f1',
    secondaryMain: '#059669', // Emerald 600
    textPrimary: '#18181b',   // Zinc 900
    textSecondary: '#52525b', // Zinc 600
    border: 'rgba(0, 0, 0, 0.06)',
    gradient: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)',
    cardGradient: 'linear-gradient(180deg, #ffffff 0%, #f9fafb 100%)'
  }
};

export const getPremiumTheme = (mode, scaledFontSize) => {
  const c = colors[mode];
  const isDark = mode === 'dark';

  return {
    palette: {
      mode,
      primary: {
        main: c.primaryMain,
        light: c.primaryLight,
        contrastText: '#fff',
      },
      secondary: {
        main: c.secondaryMain,
        contrastText: '#fff',
      },
      background: {
        default: c.bgDefault,
        paper: c.bgPaper,
      },
      text: {
        primary: c.textPrimary,
        secondary: c.textSecondary,
      },
      divider: c.border,
      // Refined, professional sidebar colors (less saturated)
      sidebarSection: {
        workoutSessions: { main: c.primaryMain, bg: alpha(c.primaryMain, 0.12) },
        myCalendars: { main: '#8b5cf6', bg: alpha('#8b5cf6', 0.12) }, // Violet
        workoutPlans: { main: c.secondaryMain, bg: alpha(c.secondaryMain, 0.12) }, // Emerald
        workoutLogs: { main: '#f59e0b', bg: alpha('#f59e0b', 0.12) }, // Amber
        locations: { main: '#06b6d4', bg: alpha('#06b6d4', 0.12) }, // Cyan
        activities: { main: '#ec4899', bg: alpha('#ec4899', 0.12) }, // Pink
        equipment: { main: '#64748b', bg: alpha('#64748b', 0.12) }, // Slate
      },
    },
    typography: {
      fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
      fontSize: scaledFontSize,
      h1: { fontWeight: 800, letterSpacing: '-0.02em' },
      h2: { fontWeight: 700, letterSpacing: '-0.01em' },
      h3: { fontWeight: 700, letterSpacing: '-0.01em' },
      h4: { fontWeight: 600 },
      h5: { fontWeight: 600 },
      h6: { fontWeight: 600, letterSpacing: '0.02em' },
      button: { fontWeight: 600, textTransform: 'none', letterSpacing: '0.01em' },
      body1: { lineHeight: 1.7 },
      body2: { lineHeight: 1.6 },
    },
    shape: {
      borderRadius: 16, // Modern, soft curves
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: `
          // @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');
          body {
            background-color: ${c.bgDefault};
          }
        `,
      },
      // Cards: Subtle gradient background for depth
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: 'none',
          },
          elevation1: {
            boxShadow: isDark 
              ? '0 4px 20px rgba(0,0,0,0.4)' 
              : '0 4px 20px rgba(0,0,0,0.05)',
            background: c.cardGradient,
            border: `1px solid ${c.border}`,
          },
          outlined: {
            borderColor: c.border,
            background: 'transparent',
          }
        }
      },
      // Buttons: Premium Gradient
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: '30px', // Full pill shape
            padding: '8px 24px',
            boxShadow: 'none',
            transition: 'all 0.2s ease-in-out',
            '&:hover': {
              transform: 'translateY(-1px)',
              boxShadow: isDark 
                ? `0 8px 20px ${alpha(c.primaryMain, 0.4)}` 
                : `0 8px 20px ${alpha(c.primaryMain, 0.25)}`,
            }
          },
          containedPrimary: {
            background: c.gradient,
            color: '#fff',
            border: 'none',
          },
          outlined: {
            borderWidth: '1.5px',
            '&:hover': {
              borderWidth: '1.5px',
            }
          }
        }
      },
      // Dialogs: Clean headers
      MuiDialogTitle: {
        styleOverrides: {
          root: {
            fontSize: '1.25rem',
            fontWeight: 700,
            padding: '24px 32px 16px',
          }
        }
      },
      MuiDialogContent: {
        styleOverrides: {
          root: {
            padding: '16px 32px 24px',
          }
        }
      },
      // Inputs: Minimalist
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: 12,
            backgroundColor: isDark ? alpha('#fff', 0.03) : alpha('#000', 0.02),
            '& fieldset': {
              borderColor: isDark ? alpha('#fff', 0.15) : alpha('#000', 0.1),
              transition: 'border-color 0.2s',
            },
            '&:hover fieldset': {
              borderColor: isDark ? alpha('#fff', 0.3) : alpha('#000', 0.3),
            },
            '&.Mui-focused fieldset': {
              borderColor: c.primaryMain,
              borderWidth: '2px',
            }
          }
        }
      },
      // Lists: Modern selection
      MuiListItemButton: {
        styleOverrides: {
          root: {
            borderRadius: 12,
            margin: '4px 8px',
            '&.Mui-selected': {
              backgroundColor: alpha(c.primaryMain, 0.15),
              color: c.primaryMain,
              '& .MuiListItemIcon-root': {
                color: c.primaryMain,
              },
              '&:hover': {
                backgroundColor: alpha(c.primaryMain, 0.25),
              }
            }
          }
        }
      },
      MuiChip: {
        styleOverrides: {
          root: {
            fontWeight: 600,
          },
          outlined: {
            borderColor: c.border,
            backgroundColor: 'transparent',
          }
        }
      },
      MuiTabs: {
        styleOverrides: {
          indicator: {
            height: 3,
            borderTopLeftRadius: 3,
            borderTopRightRadius: 3,
            background: c.gradient,
          }
        }
      },
      MuiTab: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            fontWeight: 600,
            fontSize: '0.95rem',
            minHeight: 48,
          }
        }
      }
    },
  };
};